// 📄 src/pages/auth/Login.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { Clock } from "lucide-react";
import { login } from "./services/authService";
import { useAuth } from "../../app/providers/AuthProvider";

import { routeForStatus, withLocation, WORKSPACE_HOME_PATH } from "../../app/routes/navigationPaths";

const LAST_EMAIL_KEY = "auth_last_email";
const LAST_REASON_KEY = "auth_last_reason";

export const formatLockoutCountdown = seconds => {
  const safeSeconds = Math.max(0, Math.ceil(Number(seconds) || 0));
  const minutes = Math.floor(safeSeconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(
    safeSeconds % 60
  ).padStart(2, "0")}`;
};

const TESTIMONIALS = [
  {
    quote: "Best decision we made for our customer operations this year. The AI handles most of our inbound while our team focuses on closing.",
    name: "Rahul Sharma",
    role: "Head of Growth · TechVentures India",
    initials: "RS",
    gradient: "from-emerald-500 to-teal-600",
  },
  {
    quote: "We cut our average response time from 4 hours to under 2 minutes. Our CSAT scores jumped 35% in the first month alone.",
    name: "Priya Mehta",
    role: "Customer Success Lead · Freshloop",
    initials: "PM",
    gradient: "from-teal-500 to-cyan-600",
  },
  {
    quote: "Finally a platform that doesn't treat WhatsApp as an afterthought. Broadcasting, automation, analytics — all in one place.",
    name: "Arjun Nair",
    role: "Co-founder · ShipFast Commerce",
    initials: "AN",
    gradient: "from-emerald-600 to-green-500",
  },
  {
    quote: "Our sales team went from juggling 5 tools to just XploreByte. Pipeline visibility improved overnight.",
    name: "Sneha Iyer",
    role: "VP Sales · CloudBridge Solutions",
    initials: "SI",
    gradient: "from-cyan-500 to-emerald-500",
  },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [search] = useSearchParams();
  const { refreshAuthContext } = useAuth();

  const [email, setEmail] = useState(() => {
    const fromQuery = search.get("email");
    if (fromQuery) return fromQuery;
    try {
      return localStorage.getItem(LAST_EMAIL_KEY) || "";
    } catch {
      return "";
    }
  });

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [sessionNotice, setSessionNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [temporaryRestriction, setTemporaryRestriction] = useState(null);

  const normalizedEmail = email.trim().toLowerCase();
  const isCurrentEmailLocked =
    temporaryRestriction?.kind === "account" &&
    temporaryRestriction.remainingSeconds > 0 &&
    temporaryRestriction.email === normalizedEmail;
  const isInfrastructureLimited =
    temporaryRestriction?.kind === "infrastructure" &&
    temporaryRestriction.remainingSeconds > 0;
  const isSubmissionBlocked =
    isCurrentEmailLocked || isInfrastructureLimited;

  useEffect(() => {
    let reasonFromUrl = search.get("reason");
    let reasonFromStorage = null;
    try {
      reasonFromStorage = sessionStorage.getItem(LAST_REASON_KEY);
    } catch {
      /* ignore */
    }

    const effectiveReason = reasonFromUrl || reasonFromStorage;
    if (effectiveReason === "session-expired") {
      setSessionNotice("Your session has expired. Please sign in again.");
    } else if (effectiveReason === "idle-timeout") {
      setSessionNotice(
        "Your session ended after inactivity. Please sign in again."
      );
    }
    try {
      sessionStorage.removeItem(LAST_REASON_KEY);
    } catch {
      /* ignore */
    }
  }, [search]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!temporaryRestriction?.remainingSeconds) return undefined;
    const timeout = setTimeout(() => {
      setTemporaryRestriction(current => {
        if (!current) return null;
        const remainingSeconds = Math.max(0, current.remainingSeconds - 1);
        return remainingSeconds > 0 ? { ...current, remainingSeconds } : null;
      });
    }, 1000);
    return () => clearTimeout(timeout);
  }, [temporaryRestriction]);

  const handleSubmit = async e => {
    e.preventDefault();
    if (isSubmissionBlocked) return;
    setError("");
    setSessionNotice("");
    setLoading(true);

    try {
      const res = await login(email, password);
      try {
        sessionStorage.setItem("xb_suppress_401_toast", "1");
        setTimeout(
          () => sessionStorage.removeItem("xb_suppress_401_toast"),
          4000
        );
      } catch {}

      const ctx = await refreshAuthContext();
      const statusFromCtx = ctx?.status || res?.status;

      try {
        localStorage.setItem("xb_session_stamp", String(Date.now()));
        localStorage.removeItem("messaging-pinned");
        localStorage.removeItem("messaging-archived");
        localStorage.removeItem("messaging-order");
        localStorage.setItem(LAST_EMAIL_KEY, email);
        sessionStorage.removeItem(LAST_REASON_KEY);
      } catch {}

      const candidate = search.get("redirectTo");
      const statusDestination = routeForStatus(statusFromCtx);
      const next = statusDestination !== WORKSPACE_HOME_PATH
        ? withLocation(statusDestination, location)
        :
        (candidate && candidate.startsWith("/") && !candidate.startsWith("//")
          ? candidate
          : null) || withLocation(statusDestination, location);

      navigate(next, { replace: true });
    } catch (err) {
      const response = err?.response?.data;
      if (response?.code === "AUTH_TEMPORARILY_LOCKED") {
        const remainingSeconds = Math.max(
          1,
          Math.ceil(Number(response.retryAfterSeconds) || 300)
        );
        setTemporaryRestriction({
          kind: "account",
          email: normalizedEmail,
          remainingSeconds,
        });
        setError("");
        return;
      }
      if (response?.code === "RATE_LIMITED" || err?.response?.status === 429) {
        const retryAfterHeader = err?.response?.headers?.["retry-after"];
        const remainingSeconds = Math.max(
          1,
          Math.ceil(
            Number(response?.retryAfterSeconds ?? retryAfterHeader) || 300
          )
        );
        setTemporaryRestriction({
          kind: "infrastructure",
          remainingSeconds,
        });
        setError("");
        return;
      }
      const status = err?.response?.status;
      const message = status && status < 500
        ? response?.message || "Invalid email or password."
        : "We couldn’t sign you in right now. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const errorText = String(error || "").trim();
  const errorLower = errorText.toLowerCase();
  const displayErrorText =
    errorLower.includes("not active") ||
    errorLower.includes("under review")
      ? "Your account is not active. Please contact support."
      : errorText;
  const isWarning =
    typeof error === "string" &&
    error.toLowerCase().includes("under review");
  const showErrorIcon =
    displayErrorText !== "" && /^[A-Za-z0-9]/.test(displayErrorText);

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" data-test-id="login-page">
      <div className="flex flex-col lg:flex-row w-full max-w-6xl min-h-[620px] shadow-2xl rounded-2xl overflow-hidden bg-white">

        {/* ── Left panel (matches SignupForTrial) ── */}
        <div className="lg:w-1/2 relative bg-[#f4f5f3] p-8 lg:p-10 flex flex-col justify-between overflow-hidden min-h-[620px]">
          {/* Background layers */}
          <div className="pointer-events-none absolute inset-0 z-0">
            <div className="absolute -top-20 -right-16 w-80 h-80 rounded-full bg-emerald-300 blur-[90px]" />
            <div className="absolute -bottom-24 -left-16 w-96 h-96 rounded-full bg-teal-200/90 blur-[100px]" />
            <div className="absolute top-1/2 left-1/3 w-60 h-60 rounded-full bg-emerald-200/85 blur-[80px] -translate-y-1/2" />
            <div
              className="absolute inset-0 opacity-[0.05]"
              style={{
                backgroundImage: "radial-gradient(circle, rgba(15,23,20,0.6) 1px, transparent 1px)",
                backgroundSize: "28px 28px",
              }}
            />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,#f4f5f3_95%)]" />
          </div>

          {/* Logo */}
          <div className="relative z-10 flex items-center gap-2">
            <img
              src="/logo/xplorebyte-icon.svg"
              alt="XploreByte"
              className="h-10 w-10 object-contain"
            />
            <span className="text-2xl font-semibold text-gray-900 tracking-tight">
              XploreByte
            </span>
          </div>

          {/* Center content */}
          <div className="relative z-10 flex flex-col gap-9">

            {/* Headline */}
            <div>
              <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-[0.3em] mb-4">
                WhatsApp Business Platform
              </p>
              <h2
                className="font-extrabold text-gray-900 leading-[1.08] tracking-tight"
                style={{ fontSize: "clamp(1.75rem, 2.9vw, 2.35rem)" }}
              >
                Built for businesses
                <br />
                that take{" "}
                <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">
                  WhatsApp
                </span>
                <br />
                seriously.
              </h2>
            </div>

            {/* Auto-scrolling testimonials */}
            <div className="relative" style={{ minHeight: "140px" }}>
              <svg
                className="absolute -top-2 -left-1 w-9 h-9 text-emerald-600/20"
                viewBox="0 0 32 32"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M10 8H4v8c0 4 2 6 6 6v-2c-2 0-4-1-4-4h4V8zm14 0h-6v8c0 4 2 6 6 6v-2c-2 0-4-1-4-4h4V8z" />
              </svg>
              {TESTIMONIALS.map((t, i) => (
                <div
                  key={i}
                  className="absolute inset-0 transition-all duration-700 ease-in-out"
                  style={{
                    opacity: activeTestimonial === i ? 1 : 0,
                    transform: activeTestimonial === i ? "translateY(0)" : "translateY(8px)",
                    pointerEvents: activeTestimonial === i ? "auto" : "none",
                  }}
                >
                  <p
                    className="text-gray-700 leading-relaxed pl-9"
                    style={{
                      fontFamily: "Georgia, 'Times New Roman', serif",
                      fontSize: "15px",
                      lineHeight: 1.6,
                    }}
                  >
                    {t.quote}
                  </p>
                  <div className="flex items-center gap-3 mt-5 pl-9">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${t.gradient} flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0`}>
                      {t.initials}
                    </div>
                    <div>
                      <p className="text-gray-900 text-[12px] font-semibold leading-none">
                        {t.name}
                      </p>
                      <p className="text-gray-500 text-[10.5px] mt-1">
                        {t.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {/* Dot indicators */}
              <div className="absolute bottom-0 left-9 flex items-center gap-1.5">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setActiveTestimonial(i)}
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                      activeTestimonial === i
                        ? "bg-emerald-600 w-4"
                        : "bg-gray-300 hover:bg-gray-400"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Inline metrics */}
            <div className="flex items-center gap-5 pt-5 border-t border-gray-200">
              {[
                { value: "2.4M+", label: "Messages sent" },
                { value: "98%", label: "Delivery rate" },
                { value: "300+", label: "Active teams" },
              ].map(({ value, label }, i) => (
                <div key={label} className="flex items-center gap-5">
                  <div>
                    <p className="text-gray-900 font-bold text-[15px] leading-none">{value}</p>
                    <p className="text-gray-500 text-[10.5px] mt-1.5">{label}</p>
                  </div>
                  {i < 2 && <div className="w-px h-9 bg-gray-200" />}
                </div>
              ))}
            </div>

          </div>

          {/* Bottom — trust strip */}
          <div className="relative z-10">
            <p className="text-gray-500 text-[12px] leading-snug">
              Trusted by <span className="text-gray-800 font-semibold">300+ businesses</span> across 12 countries
            </p>
          </div>
        </div>

        {/* ── Right panel — Login form ── */}
        <div className="lg:w-1/2 relative bg-white px-8 py-8 flex flex-col justify-center min-h-[620px] overflow-hidden border-l border-gray-200">
          <div className="w-full max-w-sm mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-1.5 tracking-tight">
              Welcome back
            </h2>
            <p className="text-gray-500 text-sm mb-8">
              Sign in to continue to your account.
            </p>

            {sessionNotice && (
              <div className="p-3.5 rounded-xl mb-6 text-sm font-medium bg-amber-50 text-amber-800 border border-amber-100 flex items-center">
                <Clock className="w-4 h-4 mr-2 flex-shrink-0" />
                {sessionNotice}
              </div>
            )}

            {error && (
              <div
                className={`p-3.5 rounded-xl mb-6 text-sm font-medium border flex items-center ${
                  isWarning
                    ? "bg-amber-50 text-amber-800 border-amber-100"
                    : "bg-red-50 text-red-700 border-red-100"
                }`}
              >
                {showErrorIcon && (
                  <span className="mr-2 flex-shrink-0">{isWarning ? "⚠️" : "🚫"}</span>
                )}{" "}
                {displayErrorText || error}
              </div>
            )}

            {isCurrentEmailLocked && (
              <div
                className="p-3.5 rounded-xl mb-6 bg-amber-50 text-amber-900 border border-amber-200"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold">Too many failed login attempts</p>
                    <p className="text-xs mt-1 text-amber-800">
                      Login is temporarily blocked for 5 minutes.
                    </p>
                    <p className="text-xs mt-1 font-medium">
                      Try again in {formatLockoutCountdown(temporaryRestriction.remainingSeconds)}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isInfrastructureLimited && (
              <div
                className="p-3.5 rounded-xl mb-6 bg-amber-50 text-amber-900 border border-amber-200"
                role="status"
                aria-live="polite"
              >
                <div className="flex items-start gap-2.5">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <p className="text-sm font-semibold">Too many login requests</p>
                    <p className="text-xs mt-1 font-medium text-amber-800">
                      Please try again in {formatLockoutCountdown(temporaryRestriction.remainingSeconds)}.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Email
                </label>
                <input
                  type="email"
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200 bg-gray-50 focus:bg-white text-gray-900 text-sm placeholder-gray-400"
                  placeholder="name@company.com"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    setError("");
                  }}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="w-full px-3.5 py-2.5 pr-11 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all duration-200 bg-gray-50 focus:bg-white text-gray-900 text-sm placeholder-gray-400"
                    placeholder="Enter password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    onClick={() => setShowPassword(prev => !prev)}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type="submit"
                disabled={loading || isSubmissionBlocked}
                className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm text-white transition-all duration-200 ${
                  loading || isSubmissionBlocked
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Signing in...
                  </span>
                ) : isCurrentEmailLocked ? (
                  `Try again in ${formatLockoutCountdown(temporaryRestriction.remainingSeconds)}`
                ) : isInfrastructureLimited ? (
                  `Try again in ${formatLockoutCountdown(temporaryRestriction.remainingSeconds)}`
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm text-gray-500">
                Don&apos;t have an account?{" "}
                <Link
                  to="/signup-for-trial"
                  className="font-semibold text-emerald-700 hover:text-emerald-800 hover:underline"
                >
                  Register
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
